import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { PlanogramService } from '../services/planogram.service';
import { PlanogramStatus } from '../models/planogram-status';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { ProductInspectorService } from '../services/product-inspector.service';
import { Subscription } from 'rxjs';
import { ProductReference } from "../models/layout/product-reference";
import { LayoutService } from '../services/layout.service';
import { Router } from '@angular/router';
import { ProductOrientation } from '../models/layout/product-orientation';
import { ColumnDef } from '../models/ColumnDef';
import { ColumnsSelectModalComponent } from '../modals/columns-select-modal/columns-select-modal.component';
import { ModalService } from '../services/modal.service';
import { DatePipe, NgIf } from '@angular/common';

@Component({
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger('fadeInOut', [
            state('void', style({ opacity: 0 })),
            state('*', style({ opacity: 1 })),
            transition('void <=> *', animate('300ms ease-in-out')),
        ])
    ],
    imports: [NgIf, DatePipe]
})
export class FooterComponent implements OnInit, OnDestroy  {
  public currentStatus: PlanogramStatus | null = null;
  private subscriptions: Subscription = new Subscription();
  private layoutDOS: Map<number, number> = new Map();
  public productReference: ProductReference | null = null;
  public avgDOSWeighted: number | null = null;
  Number = Number;
  ProductOrientation = ProductOrientation;

  columns: ColumnDef[] = [
    { key: 'brand', label: 'Brand', visible: true },
    { key: 'description', label: 'Description', visible: true },
    { key: 'barcode', label: 'Barcode', visible: true },
    { key: 'nationalId', label: 'National Id', visible: true },
    { key: 'uos', label: 'UoS', visible: true },
    { key: 'unit', label: 'Unit', visible: true },
    { key: 'orientation', label: 'Orientation', visible: true },
    { key: 'dos', label: 'DOS', visible: true },
    { key: 'sales', label: 'Sales (R)', visible: true },
    { key: 'volume', label: 'Volume', visible: true },
    { key: 'shelfPosition', label: 'Shelf Position', visible: true },
    { key: 'dimensions', label: 'Dimensions (W×H×D)', visible: true },
  ];

  constructor(
    public planogramService: PlanogramService,
    public layoutService: LayoutService,
    public productInspectorService: ProductInspectorService,
    public router: Router,
    private modalService: ModalService
  ) {}
  

  ngOnInit() {
    this.loadColumnSelections();

    this.subscriptions.add(
      this.planogramService.status$.subscribe((status: PlanogramStatus) => {
        this.currentStatus = status;
      })
    );
    this.subscriptions.add(
      this.layoutService.layoutDOS$.subscribe((dos) => {
        this.layoutDOS = dos;
        this.calculateAverageWeightedDOS();
      })
    )
    this.subscriptions.add(
      this.productInspectorService.inspectedProduct$.subscribe((productId) => {
        if(productId == null || productId == -1){
          this.productReference = null;
        }
        else{
          this.productReference = this.productInspectorService.getProductReference();
        }
      })
    )
  }

  ngOnDestroy() {
    // Clean up all subscriptions
    this.subscriptions.unsubscribe();
  }

  isColumnVisible(key: string): boolean {
    return this.columns.find(c => c.key === key)?.visible ?? false;
  }

  openColumnSelect() {
    this.modalService
      .openModalWithOptions(
        ColumnsSelectModalComponent,
        { centered: true, size: 'lg' },
        { data: [...this.columns], name: 'Footer Product Information' }
      )
      .then((updated: ColumnDef[] | null) => {
        if (updated) {
          this.columns = [...updated];
          this.saveColumnSelections();
        }
      });
  }

  private loadColumnSelections() {
    const saved = localStorage.getItem('footerProductColumns');
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved) as ColumnDef[];
      this.columns = this.columns.map(col => {
        const match = parsed.find(p => p.key === col.key);
        return match ? { ...col, visible: match.visible } : col;
      });
    } catch {
      console.warn('Failed to parse saved footer column settings');
    }
  }

  private saveColumnSelections() {
    localStorage.setItem('footerProductColumns', JSON.stringify(this.columns));
  }

  closeInspector() {
    this.productInspectorService.close();
  }

  getLayoutDOS(productId: number): number {
    return this.layoutDOS.get(productId) ?? 0;
  }

  private calculateAverageWeightedDOS() {
    const planogramItems : ProductReference[] = Array.from(this.layoutService.planogramItems.values());

    let totalSalesQty = 0;
    let weightedSum = 0;

    planogramItems.forEach(product => {
      const dos = this.layoutDOS.get(product.productId);
      const salesQty = product.salesData?.salesQty ?? 0;

      if (
        dos != null &&
        isFinite(dos) &&
        dos > 0 &&
        salesQty > 0
      ) {
        weightedSum += dos * salesQty;
        totalSalesQty += salesQty;
      }
    });

    this.avgDOSWeighted =
      totalSalesQty > 0 ? parseFloat((weightedSum / totalSalesQty).toFixed(1)) : null;
  }
}
